/* eslint-disable react/no-unstable-nested-components */
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Pagination, Stack, Select, MenuItem } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { alpha, styled, lighten } from '@mui/material/styles';

export default function ApprovalHierarchyGridView({ data = [], headerName }) {    

    const ODD_OPACITY = 0.2;

    const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
        [`& .${gridClasses.columnHeader}`]: {
            backgroundColor: alpha(lighten(theme.palette.primary.main, 0.5), ODD_OPACITY),
            color: '#023A82',
            paddingLeft: '10px',
            fontSize: '14px',  // Header font size set to 14px
        },
        [`& .${gridClasses.cell}`]: {
            fontSize: '13px',  // Cell font size set to 13px
        },
        [`& .${gridClasses.row}.even`]: {
            backgroundColor: '#e6eef6',
            '&:hover, &.Mui-hovered': {
                backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
                '@media (hover: none)': {
                    backgroundColor: 'transparent',
                },
            },
            '&.Mui-selected': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    ODD_OPACITY + theme.palette.action.selectedOpacity,
                ),
                '&:hover, &.Mui-hovered': {
                    backgroundColor: alpha(
                        theme.palette.primary.main,
                        ODD_OPACITY + theme.palette.action.selectedOpacity + theme.palette.action.hoverOpacity,
                    ),
                    '@media (hover: none)': {
                        backgroundColor: alpha(
                            theme.palette.primary.main,
                            ODD_OPACITY + theme.palette.action.selectedOpacity,
                        ),
                    },
                },
            },
        },
        [`& .${gridClasses.row}.odd`]: {
            backgroundColor: 'white',
            '&:hover, &.Mui-hovered': {
                backgroundColor: "#F7F7FA",
                '@media (hover: none)': {
                    backgroundColor: 'transparent',
                },
            },
        },
        [`& .bold-header`]: {
            fontWeight: 'bold',
        },
    }));


    const [pageSize, setPageSize] = useState(6);
    const [currentPage, setCurrentPage] = useState(1);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const navigate = useNavigate();
    const location = useLocation();

    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const uniqueColumns = Array.from(
        new Map(
            data.ColumnWidth?.map((column) => [column.ColumnName, column]) || []
        ).values()
    );

    const columns = uniqueColumns.map((column) => ({
        field: column.ColumnName,
        headerName: column.ColumnName,
        flex :column.ColumnWidth && parseInt(column.ColumnWidth, 10) 
    }));


    const rows = data.Data?.map((row) => ({
        id: Number(row.Id),
        ...row,
    })) || [];
    

    function shiftDataToStart(rows1, startIndex1, endIndex1) {
        const selectedRows = rows1.slice(startIndex1, endIndex1);
        const beforeSelected = rows1.slice(0, startIndex1);
        const afterSelected = rows1.slice(endIndex1);
        let returnData;

        if (selectedRows.length % pageSize === 0) {
            returnData = selectedRows.concat(beforeSelected, afterSelected);
        } else {
            returnData = selectedRows;
        }
        return returnData;
    }

    const updatedRows = shiftDataToStart(rows, startIndex, endIndex);

    useEffect(() => {
        const storedPageNumber = JSON.parse(sessionStorage.getItem('gridPageNumber'));
        if (storedPageNumber && storedPageNumber.currentUrl === location.pathname) {
            setCurrentPage(storedPageNumber.pageNumber);
        } else {
            setCurrentPage(1);
        }
    }, [data, location.pathname]);

    const handleRowDoubleClick = (row) => {
        sessionStorage.setItem('gridPageNumber', JSON.stringify({ currentUrl: location.pathname, pageNumber: currentPage }));
        navigate(`${window.location.pathname}/edit`, { state: { data: { id: row.id, pageName: headerName } } });
    };
    return (
        <div style={{ display: "flex", flexDirection: 'column', minHeight: 0, flexGrow: 1,}}>
            <div style={{ display: "flex", flexDirection: 'column', minHeight: 0, flexGrow: 1}}>
                <StripedDataGrid  localeText={{ noRowsLabel: 'Data not found' }}
                    rows={updatedRows}
                    columns={columns}
                    pageSize={pageSize}
                    disableSelectionOnClick
                    getRowId={(row) => row.id}
                    getRowClassName={(params) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? 'odd' : 'even'
                    }
                    onRowDoubleClick={(params) => handleRowDoubleClick(params.row)}
                    components={{ Pagination: null }}
                    hideFooterPagination
                    sx={{ height: '100%' }}
                />
            </div>
            {updatedRows.length > 0 &&
                <div style={{ justifySelf: 'flex-end' }} >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ mb: 0, pt: 2 }}
                    >
                        <Pagination
                            count={Math.ceil(rows.length / pageSize)}
                            variant="outlined"
                            size="small"
                            shape="rounded"
                            page={currentPage}
                            onChange={handlePageChange}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <span style={{ fontSize: '13px' }}>Rows per page:</span>
                            <Select
                                size="small"
                                sx={{
                                    maxHeight: '26px',
                                    fontSize: '13px', // This changes the font size of the displayed value
                                    '& .MuiSelect-select': {
                                        fontSize: '13px',
                                        padding: '4px 8px', // optional: adjust padding for tighter layout
                                    }
                                }}
                                MenuProps={{
                                    anchorOrigin: {
                                        vertical: 'top',       // Anchor at the top of the Select
                                        horizontal: 'left',
                                    },
                                    transformOrigin: {
                                        vertical: 'bottom',    // Menu grows upward from the bottom
                                        horizontal: 'left',
                                    },
                                    PaperProps: {
                                        sx: {
                                            fontSize: '13px',
                                        }
                                    }
                                }}
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(e.target.value);
                                    setCurrentPage(1); // Reset to first page
                                }}
                            >
                                {[6, 10, 20, 50, 100].map((size) => (
                                    <MenuItem size="small" key={size} value={size} sx={{ fontSize: '13px' }}>{size}</MenuItem>
                                ))}
                            </Select>
                        </div>



                    </Stack>
                </div>
            }
        </div>
    );
}

ApprovalHierarchyGridView.propTypes = {
    headerName: PropTypes.string.isRequired,
    data: PropTypes.shape({
        ColumnWidth: PropTypes.arrayOf(
            PropTypes.shape({
                ColumnName: PropTypes.string.isRequired,
                ColumnWidth: PropTypes.string,
                FilterType: PropTypes.string,
                ColumnValue: PropTypes.string
            })
        ),
        Data: PropTypes.arrayOf(
            PropTypes.shape({
                Id: PropTypes.number.isRequired,
                "Approval Hierarchy": PropTypes.string.isRequired,
            })
        )
    }).isRequired,
};
